import { F, T, always, curry, identity, propEq } from 'ramda';
import { Either, EitherPatterns } from 'tsmonad';

/** noop */
export const nothing = always(undefined);

/** noop => null */
export const nullish = always(null);

/** Array.filter에 사용하면 존재하는 데이터만 뽑아내고 타입 추론이 가능하도록 한다.  */
export const exist = <T>(value: T | null | undefined): value is T => !!value;

/**
 * Left/Right에 동일한 처리기를 사용한다.
 * @example
 * const entity = intermediate.caseOf(anyway(console.log));
 */
export const anyway = <L, R, T>(pattern: (lr: L | R) => T): EitherPatterns<L, R, T> => {
  return {
    left: pattern,
    right: pattern,
  };
};

/** `Either#caseOf` */
interface IFork {
  <T>(left: (l: any) => T, right: (r: any) => T): <L, R>(either: Either<L, R>) => T;
}
export const fork: IFork = (left, right) => (either: Either<any, any>) => either.caseOf({ left, right });

/** Identity over both left/right */
interface IPassThrough {
  <L = any, R = any>(either: Either<L, R>): L | R;
}
export const passThrough: IPassThrough = fork(identity, identity as any);

/** `Either#isLeft` */
export const isLeft = fork(T, F);

/** `Either.isRight` */
export const isRight = fork(F, T);

/** `Either#mapLeft` */
interface IMapLeft {
  <T, L = any, R = any>(fn: (left: L) => T, either: Either<L, R>): Either<T, R>;
  <T, L = any, R = any>(fn: (left: L) => T): (either: Either<L, R>) => Either<T, R>;
}
export const mapLeft: IMapLeft = (curry(<T, L, R>(fn: (left: L) => T, either: Either<L, R>) => {
  if (isLeft(either)) {
    const value = passThrough(either) as L;
    return Either.left(fn(value));
  }
  return either;
}) as unknown) as IMapLeft;

/**
 * ```
Left a -> undefined
Right b -> b
```
 */
export const pickRight: <R>(either: Either<any, R>) => undefined | R = fork(nothing, identity);

/**
 * key를 이용하여 object가 T 타입인지 체킹
 * @param object key 필드가 존재한지 체크할 대상
 * @param key 필드명
 */
export function interfaceOf<T>(object: Record<string, any>, key: string): object is T {
  return key in object;
}

export function call<T>(fn: () => T): T {
  return fn();
}

export const propEqTypeInferable = <T extends Record<Key, T[Key]>, Key extends keyof T>(
  key: Key,
  value: T[Key],
): ((obj: T) => boolean) => {
  return propEq(key, value);
};
